<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Importer un bloc</h3>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-group">
          <label>Image *</label>
          <input
            type="file"
            accept="image/*"
            @change="handleFileChange"
            required
            ref="fileInput"
          />
        </div>

        <div class="form-group">
          <label>Nom de l'objet *</label>
          <input v-model="formData.object_name" type="text" required />
        </div>

        <div class="form-group">
          <label>Numéro d'inventaire</label>
          <input v-model="formData.inventory_number" type="text" />
        </div>

        <div class="form-group">
          <label>Emplacement (conservation)</label>
          <textarea v-model="formData.fragment_emplacement" rows="2"></textarea>
        </div>

        <div class="form-group">
          <label>Identification sommaire</label>
          <textarea v-model="formData.fragment_identification" rows="2"></textarea>
        </div>

        <div class="form-group">
          <label>Poids estimé (kg)</label>
          <input v-model.number="formData.poids_estime_kg" type="number" step="0.01" min="0" />
        </div>

        <div class="form-group">
          <label>Description</label>
          <textarea v-model="formData.description" rows="3"></textarea>
        </div>

        <div class="form-group">
          <label>Remarques</label>
          <textarea v-model="formData.remarks" rows="2"></textarea>
        </div>

        <div v-if="error" class="error-message">{{ error }}</div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">Annuler</button>
          <button type="submit" :disabled="loading" class="btn-submit">
            {{ loading ? 'Import...' : 'Importer' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useBlocsStore } from '@stores/blocs.store';

const emit = defineEmits<{
  close: [];
  created: [];
}>();

const blocsStore = useBlocsStore();
const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const formData = reactive({
  object_name: '',
  inventory_number: '',
  fragment_emplacement: '',
  fragment_identification: '',
  poids_estime_kg: null as number | null,
  description: '',
  remarks: '',
});

const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    selectedFile.value = input.files[0];
  }
};

const handleSubmit = async () => {
  if (!selectedFile.value) {
    error.value = 'Veuillez sélectionner une image';
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Obtenir les dimensions de l'image
    const img = new Image();
    const imageUrl = URL.createObjectURL(selectedFile.value);
    img.src = imageUrl;

    await new Promise((resolve, reject) => {
      img.onload = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(null);
      };
      img.onerror = reject;
    });

    const pk = formData.poids_estime_kg;
    await blocsStore.create(
      {
        object_name: formData.object_name,
        inventory_number: formData.inventory_number.trim() || undefined,
        fragment_emplacement: formData.fragment_emplacement.trim() || undefined,
        fragment_identification: formData.fragment_identification.trim() || undefined,
        poids_estime_kg:
          pk != null && Number.isFinite(Number(pk)) ? Number(pk) : undefined,
        description: formData.description.trim() || undefined,
        remarks: formData.remarks.trim() || undefined,
        image_width: img.width,
        image_height: img.height,
      },
      selectedFile.value
    );

    emit('created');
  } catch (err: any) {
    error.value = err.message || 'Erreur lors de l\'import';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #2a2a2a;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #444;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #e0e0e0;
}

.btn-close {
  background: none;
  border: none;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #e0e0e0;
}

.modal-body {
  padding: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-family: inherit;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4a9eff;
}

.error-message {
  color: #ff6b6b;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #444;
}

.btn-cancel,
.btn-submit {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-cancel {
  background-color: #444;
  color: #e0e0e0;
}

.btn-cancel:hover {
  background-color: #555;
}

.btn-submit {
  background-color: #4a9eff;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background-color: #3a8eef;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
